// frontend/src/components/SeccionFacturasPaciente.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { fechaSegura, euros } from '../utils/formatos';
import { PictureAsPdf as PdfIcon, ReceiptLong as ReceiptIcon } from '@mui/icons-material';

const SeccionFacturasPaciente = ({ pacienteId }) => {
    const [facturas, setFacturas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalGastado, setTotalGastado] = useState(0);

    const cargarFacturasPaciente = useCallback(async () => {
        if (!pacienteId) return;
        try {
            setLoading(true);
            // 🎯 Solicitamos las facturas filtradas rigurosamente por este paciente
            const res = await api.get(`/facturas?paciente=${pacienteId}`);

            const payload = res?.data || res;
            const lista = Array.isArray(payload) ? payload : (payload?.data || payload?.docs || []);

            setFacturas(lista);

            // Calcular el histórico total del paciente (omitiendo anuladas)
            const sumatoria = lista.reduce((sum, f) => f.estado !== 'anulada' ? sum + Number(f.total || 0) : sum, 0);
            setTotalGastado(sumatoria);
        } catch (err) {
            console.error("Error cargando historial financiero del paciente:", err);
            setFacturas([]);
        } finally {
            setLoading(false);
        }
    }, [pacienteId]);

    useEffect(() => {
        cargarFacturasPaciente();
    }, [cargarFacturasPaciente]);

    // 🚀 BLINDAJE BINARIO: Visor e impresión de PDF sin corrupción de codificación
    const handleVerDescargarPDF = async (facturaId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/facturas/${facturaId}/pdf`, {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) throw new Error("Error en la descarga del flujo binario");

            const blob = await response.blob();
            const urlTemporal = window.URL.createObjectURL(blob);

            const nuevaPestana = window.open(urlTemporal, '_blank');
            if (!nuevaPestana) {
                const enlaceDescarga = document.createElement('a');
                enlaceDescarga.href = urlTemporal;
                enlaceDescarga.download = `factura-${facturaId}.pdf`;
                document.body.appendChild(enlaceDescarga);
                enlaceDescarga.click();
                document.body.removeChild(enlaceDescarga);
            }
            setTimeout(() => window.URL.revokeObjectURL(urlTemporal), 100);
        } catch (error) {
            console.error("Fallo de streaming del PDF contable:", error);
            alert("No se pudo cargar el archivo PDF de la factura.");
        }
    };

    return (
        <div className="space-y-6">
            {/* PANEL DE METRICAS DEL PACIENTE */}
            <div className="p-6 bg-slate-900 rounded-3xl text-white flex justify-between items-center shadow-md">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-white/10 rounded-2xl text-emerald-400">
                        <ReceiptIcon fontSize="medium" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Historial de Cargos</h3>
                        <p className="text-xs text-slate-400">Total facturado y liquidado en la clínica</p>
                    </div>
                </div>
                <div className="text-right">
                    <span className="text-xs font-bold text-slate-400 block uppercase">Volumen Contable</span>
                    <span className="text-2xl font-black text-white tracking-tight">{euros(totalGastado)}</span>
                </div>
            </div>

            {/* TABLA DE FACTURAS */}
            <div className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">Fecha</th>
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">Concepto Principal</th>
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">Método</th>
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase text-right">Importe</th>
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase text-center">Acciones</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td colSpan="5" className="px-6 py-4"><div className="h-5 bg-slate-100 rounded w-full"></div></td>
                                </tr>
                            ))
                        ) : facturas.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-8 text-center text-sm font-medium text-slate-400 italic">
                                    Este paciente no registra transacciones comerciales ni recibos emitidos.
                                </td>
                            </tr>
                        ) : (
                            facturas.map((f) => (
                                <tr key={f.id || f._id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-4 text-xs font-bold text-slate-600">
                                        {fechaSegura(f.fechaCreacion, 'dd/MM/yyyy - HH:mm', '--/--')}
                                    </td>
                                    <td className="px-6 py-4 text-sm font-bold text-slate-800">
                                        {f.conceptos?.[0]?.descripcion || 'Tratamiento / Servicio Clínico'}
                                    </td>
                                    <td className="px-6 py-4">
                                            <span className={`inline-flex px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${f.metodoPago === 'efectivo' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {f.metodoPago || 'tarjeta'}
                                            </span>
                                    </td>
                                    <td className={`px-6 py-4 text-right font-black text-sm ${f.estado === 'anulada' ? 'text-red-400 line-through' : 'text-slate-900'}`}>
                                        {euros((f.total || 0))}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <button
                                            onClick={() => handleVerDescargarPDF(f.id || f._id)}
                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors focus:outline-none"
                                            title="Ver factura PDF"
                                        >
                                            <PdfIcon fontSize="small" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SeccionFacturasPaciente;